import { FC, useEffect, useRef, useState } from "react";
import {
  Mesh,
  DoubleSide,
  NormalBufferAttributes,
  BufferGeometry,
} from "three";
import { useLaserScanScene } from "../../providers/LaserScanSceneProvider";
import { ThreeEvent } from "@react-three/fiber";
import { UNITS } from "~/constants/unit";
import { getUnit } from "~/helpers/userPreference";
import { LIVE_QUADRANT_ORDER } from "../../constant/laserScene";
import { useBVH } from "@react-three/drei";
import { useLaserSceneWorker } from "../../hooks/useLaserSceneWorker";

interface LaserMeshProps {
  tooltipRef: React.MutableRefObject<HTMLSpanElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  isExpanded: boolean;
  clearTooltip: () => void;
}

const LaserMesh: FC<LaserMeshProps> = ({
  tooltipRef,
  canvasRef,
  isExpanded,
  clearTooltip,
}) => {
  const meshRef = useRef<Mesh>();

  const { heatData } = useLaserScanScene();
  const { calculateDelaunayMesh } = useLaserSceneWorker();
  const [meshGeometries, setMeshGeometries] =
    useState<BufferGeometry<NormalBufferAttributes> | null>();

  useBVH(meshRef);

  useEffect(() => {
    if (!heatData?.geometry) return;

    calculateDelaunayMesh(heatData).then((result) => {
      setMeshGeometries(result as BufferGeometry<NormalBufferAttributes>);
    });
  }, [heatData, calculateDelaunayMesh]);

  const hoverHandler = (event: ThreeEvent<PointerEvent>) => {
    if (!event.intersections || event.intersections.length === 0) {
      return;
    }

    const intersection = event.intersections[0];
    const isShellIntersection = intersection.object.userData.isShell;

    if (isShellIntersection) {
      return;
    }

    const vertexIndex = intersection.face?.a || 0;
    const geometry = (intersection.object as Mesh).geometry;

    const thickness = geometry.attributes.pointThicknessMap.array[vertexIndex];
    const quadrant =
      LIVE_QUADRANT_ORDER.indexOf(
        geometry.attributes.pointQuadrantMap.array[vertexIndex],
      ) + 1;
    const course = geometry.attributes.pointRowMap.array[vertexIndex] + 1;
    const mainZone =
      geometry.attributes.pointMainZoneMap.array[vertexIndex] === 0
        ? "Bottom"
        : "Sidewall";

    requestAnimationFrame(() => {
      if (tooltipRef.current && canvasRef.current) {
        canvasRef.current.style.cursor = "crosshair";
        tooltipRef.current.style.display = "unset";
        tooltipRef.current.style.left = `${event.offsetX - 25}px`;
        tooltipRef.current.style.top = `${
          event.offsetY + (isExpanded ? -125 : -100)
        }px`;
        tooltipRef.current.innerHTML = `<div>${thickness?.toFixed(1)}${getUnit(
          UNITS.MILLIMETER,
        )}</div><div style="font-size: 14px">Course: ${course}</div> <div style="font-size: 14px">Clock: ${quadrant}</div><div style="font-size: 14px">(${mainZone})</div>`;
      }
    });
  };

  return (
    <>
      {meshGeometries && (
        <mesh
          ref={(mesh) => (meshRef.current = mesh || undefined)}
          rotation={[0, 0, Math.PI / 2]}
          raycast={Mesh.prototype.raycast}
          name={"mesh"}
          onPointerMove={hoverHandler}
          onPointerLeave={clearTooltip}
        >
          <bufferGeometry attach="geometry" {...meshGeometries} />
          <meshPhongMaterial
            attach="material"
            vertexColors
            side={DoubleSide}
            flatShading
          />
        </mesh>
      )}
    </>
  );
};
export default LaserMesh;
